import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, View, TouchableOpacity } from "react-native";
import { Text } from "react-native-paper";

import type { ComplianceStatusType } from "@/lib/compliance-api";
import { colors, fonts, radius, spacing } from "@/lib/theme";

/** Icon + color per the four mobile compliance buckets. */
export const STATUS_VISUAL: Record<
    ComplianceStatusType,
    { icon: keyof typeof MaterialCommunityIcons.glyphMap; fg: string; bg: string }
> = {
    issue: { icon: "alert-circle", fg: colors.error500, bg: colors.error50 },
    expiringSoon: { icon: "clock-alert-outline", fg: colors.warning600, bg: colors.warning50 },
    pending: { icon: "clock-outline", fg: colors.info500, bg: colors.info50 },
    compliant: { icon: "check-circle", fg: colors.success500, bg: colors.success50 }
};

export function ComplianceCircleIcon({
    statusType,
    size = 40
}: {
    statusType: ComplianceStatusType;
    size?: number;
}) {
    const v = STATUS_VISUAL[statusType];
    return (
        <View
            style={[
                styles.circle,
                { width: size, height: size, borderRadius: size / 2, backgroundColor: v.bg }
            ]}
        >
            <MaterialCommunityIcons name={v.icon} size={size * 0.55} color={v.fg} />
        </View>
    );
}

/** One requirement card on the My Compliance / detail screens. */
export function ComplianceItemCard({
    name,
    subtitle,
    statusType,
    rejectionReason,
    onPress
}: {
    name: string;
    subtitle: string;
    statusType: ComplianceStatusType;
    rejectionReason?: string | null;
    onPress?: () => void;
}) {
    return (
        <TouchableOpacity
            style={styles.card}
            activeOpacity={onPress ? 0.7 : 1}
            onPress={onPress}
            disabled={!onPress}
        >
            <ComplianceCircleIcon statusType={statusType} />
            <View style={styles.body}>
                <Text style={styles.title}>{name}</Text>
                <Text style={styles.subtitle}>{subtitle}</Text>
                {rejectionReason ? (
                    <View style={styles.rejectBadge}>
                        <Text style={styles.rejectText}>{rejectionReason}</Text>
                    </View>
                ) : null}
            </View>
            {onPress ? (
                <MaterialCommunityIcons
                    name="chevron-right"
                    size={22}
                    color={colors.textTertiary}
                />
            ) : null}
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    card: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: spacing.base
    },
    circle: {
        alignItems: "center",
        justifyContent: "center"
    },
    body: {
        flex: 1,
        gap: 2
    },
    title: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    subtitle: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    rejectBadge: {
        marginTop: 6,
        alignSelf: "flex-start",
        backgroundColor: colors.error50,
        borderWidth: 1,
        borderColor: colors.error400,
        borderRadius: radius.xs,
        paddingHorizontal: spacing.sm,
        paddingVertical: 3
    },
    rejectText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.error600
    }
});
